import { createUseVariantStyle } from '@bonusly/ds-hooks';
import { rem } from '@bonusly/ds-utils';

import { ButtonColorScheme, ButtonSize, ButtonVariant } from '../types';

interface UseVariantStyleProps {
	colorScheme: ButtonColorScheme;
	size?: ButtonSize;
	variant?: ButtonVariant;
}

const switchColor = (props: { colorScheme: ButtonColorScheme }) => {
	const color = (primary: string, secondary: string, danger: string) => {
		switch (props.colorScheme) {
			case 'primary':
				return primary;
			case 'danger':
				return danger;
			default:
				return secondary;
		}
	};

	color.toString = () => props.colorScheme;

	return color;
};

/**
 * Button 2.0 variant styles
 *
 * Size defines base dimensions and typography.
 * Padding and gap are calculated dynamically in the Button component
 * based on icon presence to implement the two-layer padding model.
 */
export const useVariantStyle = createUseVariantStyle<UseVariantStyleProps>({
	size: {
		xs: {
			borderRadius: 'sm',
			fontSize: 'sm',
			h: rem(24),
			lineHeight: 1.667,
			minW: 'auto',
		},
		sm: {
			borderRadius: 'sm',
			fontSize: 'sm',
			h: rem(32),
			lineHeight: 1.667,
			minW: 'auto',
		},
		md: {
			borderRadius: 'md',
			fontSize: 'md',
			h: rem(40),
			lineHeight: 1.714,
			minW: 'auto',
		},
		lg: {
			borderRadius: 'md',
			fontSize: 'md',
			h: rem(44),
			lineHeight: 1.714,
			minW: 'auto',
		},
		xl: {
			borderRadius: 'md',
			fontSize: 'lg',
			h: rem(48),
			lineHeight: 1.75,
			minW: 'auto',
		},
	},
	variant: {
		solid: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: color('primary.bg', 'secondary.bg', 'danger.bg'),
				color: color('text.onColor', 'secondary.fgOnSurface', 'text.onColor'),
				_hover: {
					bg: color('primary.bgHover', 'secondary.bgHover', 'danger.bgHover'),
					_disabled: {
						bg: 'secondary.bgAlpha',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: color('primary.bgActive', 'secondary.bgActive', 'danger.bgActive'),
				},
				_disabled: {
					bg: 'secondary.bgAlpha',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		alpha: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: color('primary.bgAlpha', `${color}.bgAlpha`, 'danger.bgAlpha'),
				color: color('primary.fgStrong', 'secondary.fg', 'danger.fgStrong'),
				_hover: {
					bg: color('primary.bgAlphaHover', `${color}.bgAlphaHover`, 'danger.bgAlphaHover'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
					_disabled: {
						bg: 'secondary.bgAlpha',
						color: 'text.disabled',
						cursor: 'not-allowed',
					},
				},
				_active: {
					bg: color('primary.bgAlphaActive', `${color}.bgAlphaActive`, 'danger.bgAlphaActive'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
				},
				_disabled: {
					bg: 'secondary.bgAlpha',
					color: 'text.disabled',
				},
			};
		},
		ghost: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fgStrong', 'secondary.fg', 'danger.fgStrong'),
				_hover: {
					bg: color('primary.bgAlpha', `${color}.bgAlpha`, 'danger.bgAlpha'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
					_disabled: {
						bg: 'transparent',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: color('primary.bgAlphaHover', `${color}.bgAlphaHover`, 'danger.bgAlphaHover'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
				},
				_disabled: {
					bg: 'transparent',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		link: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fg', 'text.softest', 'danger.fg'),
				h: 'auto',
				p: 0,
				verticalAlign: 'baseline',
				_hover: {
					color: color('primary.fgStrong', 'text.default', 'danger.fgStrong'),
					textDecoration: 'underline',
					_disabled: {
						color: 'text.disabled',
					},
				},
				_active: {
					color: color('primary.fgStronger', 'text.default', 'danger.fgStronger'),
				},
				_disabled: {
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		outline: ({ props }) => {
			const color = switchColor(props);

			// Primary uses primary.fg and scales, secondary uses border.default
			const getBorderColor = () => {
				switch (props.colorScheme) {
					case 'primary':
						return 'primary.fg';
					case 'secondary':
						return 'border.default';
					case 'danger':
						return 'danger.fgStrong';
					default:
						return 'border.default';
				}
			};

			const getColor = () => {
				switch (props.colorScheme) {
					case 'primary':
						return 'primary.fg';
					case 'secondary':
						return 'secondary.fg';
					case 'danger':
						return 'danger.fgStrong';
					default:
						return 'secondary.fg';
				}
			};

			return {
				bg: 'transparent',
				color: getColor(),
				borderColor: getBorderColor(),
				borderWidth: 'thin',
				_hover: {
					bg: color('primary.bgAlpha', `${color}.bgAlpha`, 'danger.bgAlpha'),
					color: color('primary.fgStrong', 'secondary.fgActive', 'danger.fgStrong'),
					borderColor: color('primary.fgStrong', 'border.default', 'danger.fgStrong'),
					_disabled: {
						bg: 'transparent',
						color: 'text.disabled',
						borderColor: 'border.default',
					},
				},
				_active: {
					bg: color('primary.bgAlphaHover', `${color}.bgAlphaHover`, 'danger.bgAlphaHover'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
					borderColor: color('primary.fgStronger', 'border.default', 'danger.fgStronger'),
				},
				_disabled: {
					bg: 'transparent',
					color: 'text.disabled',
					borderColor: 'border.default',
					cursor: 'not-allowed',
				},
			};
		},
		subtle: () => {
			return {
				bg: 'bg.subtle',
				color: 'text.default',
				_hover: {
					bg: 'bg.soft',
					color: 'text.default',
					_disabled: {
						bg: 'bg.subtle',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: 'secondary.bgAlphaHover',
					color: 'text.default',
				},
				_disabled: {
					bg: 'bg.subtle',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
	},
});
